/**
 * Web-platform `VisibilityController` — IntersectionObserver-backed.
 *
 * Constructs an IntersectionObserver over the provided root with
 * `threshold: 0` (FR3-aligned — any pixel visible counts).
 *
 * The factory takes an `observerFactory` so SSR / test harnesses can
 * inject a stubbed observer; in production the caller passes
 * `(cb) => new IntersectionObserver(cb)`.
 *
 * The RN equivalent (T6006) binds onto `FlatList`'s `viewabilityConfig`
 * and is constructed via the PlatformAdapter — see `subject-registry.ts`
 * `installLifecycleListeners` for the shared shape.
 */

import type { TrackedElementSet, Unbind, VisibilityController } from '../visibility-controller.js';

// Re-export the platform-neutral types for callers that import the
// factory and types together. Avoids forcing every consumer to do two
// imports.
export type { TrackedElementSet, Unbind, VisibilityController };

export function createBrowserVisibilityController(
  observerFactory: (cb: IntersectionObserverCallback) => IntersectionObserver,
): { controller: VisibilityController; tracked: TrackedElementSet } {
  const elementToCallback = new Map<Element, (intersecting: boolean) => void>();

  const observer = observerFactory((entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
      const cb = elementToCallback.get(entry.target);
      if (cb !== undefined) cb(entry.isIntersecting);
    }
  });

  const trackedView: TrackedElementSet = {
    get size(): number {
      return elementToCallback.size;
    },
    values: (): IterableIterator<Element> => elementToCallback.keys(),
    has: (el: Element): boolean => elementToCallback.has(el),
  };

  const controller: VisibilityController = {
    bind(element: Element, onIntersectChange: (intersecting: boolean) => void): Unbind {
      elementToCallback.set(element, onIntersectChange);
      observer.observe(element);
      let unbound = false;
      return (): void => {
        if (unbound) return;
        unbound = true;
        observer.unobserve(element);
        elementToCallback.delete(element);
      };
    },
    takeRecords(): IntersectionObserverEntry[] {
      return observer.takeRecords();
    },
    reobserveAll(): void {
      observer.takeRecords();
      for (const el of elementToCallback.keys()) {
        observer.unobserve(el);
        observer.observe(el);
      }
    },
  };

  return { controller, tracked: trackedView };
}
